/**
 * Activity Mini Feed
 * 
 * Shows recent activity logs in a compact format
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

function ActivityMiniFeed({ activities, isAdmin }) {
  const navigate = useNavigate();

  const getCategoryIcon = (category) => {
    const icons = {
      'auth': '🔐',
      'admin': '⚙️',
      'senior': '👴',
      'reminder': '⏰',
      'device': '📱',
      'system': '💻'
    };
    return icons[category] || '📊';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'auth': 'text-cyan-400',
      'admin': 'text-orange-400',
      'senior': 'text-purple-400',
      'reminder': 'text-blue-400',
      'device': 'text-green-400',
      'system': 'text-neutral-400'
    };
    return colors[category] || 'text-neutral-400';
  };

  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `vor ${days} Tag${days > 1 ? 'en' : ''}`;
    if (hours > 0) return `vor ${hours} Stunde${hours > 1 ? 'n' : ''}`;
    if (minutes > 0) return `vor ${minutes} Minute${minutes > 1 ? 'n' : ''}`;
    return 'gerade eben';
  };

  const getActivityDescription = (activity) => {
    // Handle old format
    if (activity.description) {
      return activity.description;
    }

    // Handle new format
    const actor = activity.actor?.name || 'Jemand';
    const action = activity.action || '';
    const target = activity.target?.name || '';

    // Generate human-readable description
    const descriptions = {
      'login_success': `${actor} hat sich angemeldet`,
      'login_failed': `Fehlgeschlagener Login-Versuch`,
      'logout': `${actor} hat sich abgemeldet`,
      'created': target ? `${actor} hat ${target} erstellt` : `${actor} hat etwas erstellt`,
      'updated': target ? `${actor} hat ${target} aktualisiert` : `${actor} hat etwas aktualisiert`,
      'deleted': target ? `${actor} hat ${target} gelöscht` : `${actor} hat etwas gelöscht`,
      'photo_updated': target ? `Foto von ${target} wurde aktualisiert` : 'Foto wurde aktualisiert',
      'paired': target ? `Gerät "${target}" wurde verbunden` : 'Gerät wurde verbunden',
      'paired_via_wizard': target ? `Gerät "${target}" wurde über Wizard verbunden` : 'Gerät wurde verbunden',
      'online': target ? `Gerät "${target}" ist online` : 'Gerät ist online',
      'offline': target ? `Gerät "${target}" ist offline` : 'Gerät ist offline',
      'reminder_triggered': target ? `Erinnerung "${target}" wurde ausgelöst` : 'Erinnerung wurde ausgelöst',
      'member_role_changed': target ? `Rolle von ${target} wurde geändert` : 'Rolle wurde geändert',
    };

    return descriptions[action] || `${actor}: ${action}`;
  };

  if (!activities || activities.length === 0) {
    return (
      <div className="bg-neutral-900/80 backdrop-blur-xl rounded-2xl border border-neutral-700/50 p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-2xl">📊</span>
          <h3 className="text-lg font-bold text-white">Aktivität</h3>
        </div>
        
        <div className="text-center py-8">
          <p className="text-neutral-400">Noch keine Aktivitäten</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-neutral-900/80 backdrop-blur-xl rounded-2xl border border-neutral-700/50 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">📊</span>
          <h3 className="text-lg font-bold text-white">Aktivität</h3>
        </div>
      </div>

      {/* Activity List */}
      <div className="space-y-3 mb-4">
        {activities.map((activity, idx) => {
          const category = activity.category || 'system';
          const icon = getCategoryIcon(category);
          const color = getCategoryColor(category);
          
          return (
            <div
              key={activity.id || `activity-${idx}`}
              className="flex items-start gap-3 p-3 bg-neutral-800/50 rounded-xl hover:bg-neutral-800/70 transition-colors"
            >
              <span className={`text-xl ${color}`}>{icon}</span>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white mb-1 leading-snug">
                  {getActivityDescription(activity)}
                </p>
                <p className="text-xs text-neutral-500">
                  {formatRelativeTime(activity.created_at)}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* View All Link (Admin only) */}
      {isAdmin && (
        <button
          onClick={() => navigate('/family/admin/activity')}
          className="w-full px-4 py-2 bg-neutral-700 text-white rounded-xl hover:bg-neutral-600 transition-colors text-sm font-medium"
        >
          Alle Aktivitäten anzeigen
        </button>
      )}
    </div>
  );
}

export default ActivityMiniFeed;
